import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { reportsAPI } from '@/api/reports';
import { showError, showSuccess } from '@/utils/errorHandler';
import { formatCurrency, formatPercent, formatDateShort } from '@/utils/formatters';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// UI Components
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

// Icons
import {
  ArrowLeft,
  FileBarChart,
  Filter,
  Table,
  Download,
  CalendarIcon,
  RefreshCw,
  Save,
  FolderOpen,
  Trash2,
  BarChart3,
  AlertTriangle,
  TrendingUp,
  Users,
  PieChart,
  TableProperties,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

// Finance Dashboard (P0 BI-ADVANCED Finance Control)
import FinanceDashboard from '@/components/reports/FinanceDashboard';

// Resizable Table (Excel-like column resize & reorder)
import ResizableTable from '@/components/reports/ResizableTable';

// === CONSTANTS ===

const HEALTH_CONFIG = {
  green: { label: 'В графике', emoji: '🟢', color: 'bg-green-100 text-green-800' },
  yellow: { label: 'Риск', emoji: '🟡', color: 'bg-yellow-100 text-yellow-800' },
  red: { label: 'Критично', emoji: '🔴', color: 'bg-red-100 text-red-800' },
};

// Workload thresholds (per ТЗ BI-ADVANCED)
const WORKLOAD_CONFIG = {
  underloaded: { label: 'Недогруз', emoji: '⚪', color: 'bg-gray-100 text-gray-600', range: '<5' },
  normal: { label: 'Норма', emoji: '🟢', color: 'bg-green-100 text-green-700', range: '5-8' },
  elevated: { label: 'Повышенная', emoji: '🟡', color: 'bg-yellow-100 text-yellow-700', range: '9-12' },
  overloaded: { label: 'Перегруз', emoji: '🔴', color: 'bg-red-100 text-red-700', range: '>12' },
};

// === Колонки соответствуют полям из формы "Новый проект" (ProjectDetailPage.js) ===
// БЛОК 1: ПАСПОРТ (12 полей)
// БЛОК 2: ОЦЕНКА (3 поля)
// БЛОК 3: ОБЯЗАТЕЛЬСТВА (9 полей)
// БЛОК 4: РИСКИ (3 поля) - виден всем
// БЛОКИ 5-8: Финансы (скрыто для Senior Manager)
// Итого для Senior Manager: 26 полей (без description)

const TABLE_COLUMNS = [
  // === БЛОК 1: ПАСПОРТ (12 полей, без description = 11) ===
  { key: 'bitrix_task_id', label: 'ID Bitrix', default: false, block: 1 },
  { key: 'year', label: 'Год', default: false, block: 1 },
  { key: 'client_name', label: 'Заказчик', default: true, block: 1 },
  { key: 'name', label: 'Наименование проекта', default: true, block: 1 },
  { key: 'description', label: 'Описание проекта', default: false, block: 1 },
  { key: 'current_stage', label: 'Текущий этап', default: false, block: 1 },
  { key: 'lifecycle_phase', label: 'Фаза (PRESALE/EXECUTION)', default: false, block: 1 },
  { key: 'manager', label: 'Менеджер', default: true, block: 1 },
  { key: 'vendor_name', label: 'Вендор', default: true, block: 1 },
  { key: 'supplier_name', label: 'Поставщик', default: false, block: 1 },
  { key: 'project_status', label: 'Статус проекта', default: true, block: 1 },
  { key: 'client_budget_with_vat', label: 'Бюджет заказчика с НДС', default: false, block: 1 },
  { key: 'co_executors', label: 'Соисполнители', default: false, block: 1 },
  { key: 'ts_status', label: 'Статус ТС', default: false, block: 1 },
  { key: 'ts_deadline', label: 'Срок ТС', default: false, block: 1 },
  { key: 'start_date', label: 'Дата начала', default: false, block: 1 },
  { key: 'updated_at', label: 'Дата обновления', default: false, block: 1 },

  // === БЛОК 2: ОЦЕНКА (3 поля) ===
  { key: 'kp_amount_with_vat', label: 'Сумма КП с НДС', default: false, block: 2 },
  { key: 'gpl_project_with_vat', label: 'GPL проекта с НДС', default: false, block: 2 },
  { key: 'entry_cost_with_vat', label: 'Вход проекта с НДС', default: false, block: 2 },

  // === БЛОК 3: ОБЯЗАТЕЛЬСТВА (9 полей) ===
  { key: 'expected_end_date', label: 'Дедлайн проекта', default: true, block: 3 },
  { key: 'contract_amount_with_vat', label: 'Сумма договора с НДС', default: false, block: 3 },
  { key: 'contract_sign_date', label: 'Дата подписания договора', default: false, block: 3 },
  { key: 'contract_close_date', label: 'Дата закрытия договора', default: false, block: 3 },
  { key: 'vendor_payment_plan_date', label: 'Дата оплаты Вендору (план)', default: false, block: 3 },
  { key: 'vendor_payment_fact_amount', label: 'Оплата Вендору (факт)', default: false, block: 3 },
  { key: 'client_payment_fact_date', label: 'Дата оплаты Заказчиком', default: false, block: 3 },
  { key: 'delivery_eta', label: 'Ожидаемая доставка', default: false, block: 3 },
  { key: 'installation_start', label: 'Начало монтажа', default: false, block: 3 },

  // === БЛОК 4: РИСКИ (3 поля) - видно всем ===
  { key: 'risk_category', label: 'Категория риска', default: false, block: 4 },
  { key: 'risk_probability', label: 'Вероятность риска', default: false, block: 4 },
  { key: 'risk_impact', label: 'Влияние риска', default: false, block: 4 },

  // === БЛОК 5: ЗАТРАТЫ (9 полей) - только Finance Admin / Admin ===
  { key: 'expenses_equipment', label: 'Затраты: Оборудование', default: false, block: 5, finance: true },
  { key: 'expenses_logistics', label: 'Затраты: Логистика', default: false, block: 5, finance: true },
  { key: 'expenses_other', label: 'Затраты: Прочее', default: false, block: 5, finance: true },
  { key: 'expenses_installation', label: 'Затраты: Монтаж', default: false, block: 5, finance: true },
  { key: 'expenses_overtime', label: 'Переработки', default: false, block: 5, finance: true },
  { key: 'expenses_insurance', label: 'Страхование', default: false, block: 5, finance: true },
  { key: 'expenses_credit', label: 'Кредит', default: false, block: 5, finance: true },
  { key: 'expenses_partnership', label: '% за партнерство', default: false, block: 5, finance: true },
  { key: 'expenses_penalty', label: 'Пеня', default: false, block: 5, finance: true },

  // === БЛОК 6: СУБПОДРЯД И НАЛОГИ (3 поля) - только Finance Admin / Admin ===
  { key: 'subcontract_percent', label: '% субподряда', default: false, block: 6, finance: true },
  { key: 'subcontract_amount', label: 'Сумма субподряда', default: false, block: 6, finance: true },
  { key: 'taxes_amount', label: 'Налоги', default: false, block: 6, finance: true },

  // === БЛОК 7: ИТОГОВАЯ КАЛЬКУЛЯЦИЯ (4 поля) - только Finance Admin / Admin ===
  { key: 'result_margin', label: 'Маржа, тг', default: false, block: 7, finance: true },
  { key: 'result_margin_percent', label: 'Маржа, %', default: false, block: 7, finance: true },
  { key: 'result_taxes', label: 'Налоги (итог)', default: false, block: 7, finance: true },
  { key: 'result_gross_profit', label: 'Валовая прибыль', default: false, block: 7, finance: true },

  // === БЛОК 8: ПЛАН/ФАКТ (3 поля) - только Finance Admin / Admin ===
  { key: 'budget_plan', label: 'План (бюджет)', default: false, block: 8, finance: true },
  { key: 'budget_fact', label: 'Факт (получено)', default: false, block: 8, finance: true },
  { key: 'costs_fact', label: 'Затраты (факт)', default: false, block: 8, finance: true },

  // === БЛОК 9: КОММЕНТАРИИ (Sprint D) ===
  { key: 'status_comment_internal', label: 'Комментарий (внутр.)', default: false, block: 9, finance: true },
  { key: 'status_comment_external', label: 'Комментарий (клиентский)', default: false, block: 9 },
];

const PIVOT_ROW_OPTIONS = [
  { value: 'client_name', label: 'Заказчик' },
  { value: 'responsible_name', label: 'Ответственный' },
  { value: 'vendor_name', label: 'Вендор' },
];

const PIVOT_VALUE_OPTIONS = [
  { value: 'kp_amount_with_vat', label: 'КП с НДС' },
  { value: 'contract_amount_with_vat', label: 'Договор с НДС' },
  { value: 'budget_wo_vat', label: 'Бюджет без НДС (legacy)' },
  { value: 'margin', label: 'Сумма маржи' },
  { value: 'count', label: 'Кол-во проектов' },
];

const AGGREGATION_OPTIONS = [
  { value: 'sum', label: 'Сумма' },
  { value: 'avg', label: 'Среднее' },
  { value: 'count', label: 'Количество' },
];

// Finance pivot values hidden for Senior Manager
const FINANCE_PIVOT_VALUES = ['margin'];

// Period options (P2 BI-ADVANCED)
const PERIOD_OPTIONS = [
  { value: 'all', label: 'Всё время' },
  { value: 'month', label: 'Этот месяц' },
  { value: 'quarter', label: 'Этот квартал' },
  { value: 'year', label: 'Этот год' },
];

// Preset Pivot Templates (P2 BI-ADVANCED)
const PIVOT_TEMPLATES = [
  {
    id: 'by_manager',
    name: 'По менеджерам',
    icon: '👤',
    config: { rows: ['responsible_name'], values: ['count', 'kp_amount_with_vat'], aggregation: 'sum' },
  },
  {
    id: 'by_client',
    name: 'По клиентам',
    icon: '🏢',
    config: { rows: ['client_name'], values: ['count', 'kp_amount_with_vat', 'margin'], aggregation: 'sum' },
  },
  {
    id: 'by_vendor',
    name: 'По вендорам',
    icon: '📦',
    config: { rows: ['vendor_name'], values: ['count', 'kp_amount_with_vat'], aggregation: 'sum' },
  },
  {
    id: 'financial',
    name: 'Финансовый срез',
    icon: '💰',
    config: { rows: ['client_name', 'responsible_name'], values: ['kp_amount_with_vat', 'contract_amount_with_vat', 'margin'], aggregation: 'sum' },
  },
];

// === COLLAPSIBLE WORKLOAD PANEL ===

const CollapsibleWorkloadPanel = ({ teamWorkload, filters, onFilterChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Count problem managers
  const problemManagers = teamWorkload.managers?.filter(
    m => m.workload_status === 'overloaded' || m.workload_status === 'elevated'
  ) || [];
  const overloadedCount = teamWorkload.summary?.overloaded || 0;
  const elevatedCount = teamWorkload.summary?.elevated || 0;

  return (
    <div className="border rounded-lg bg-white overflow-hidden">
      {/* Collapsed header - always visible */}
      <div className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors">
        {/* Left part - clickable to expand/collapse */}
        <div
          className="flex items-center gap-3 cursor-pointer flex-1"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Users className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Нагрузка команды</span>

          {/* Quick summary badges */}
          {(overloadedCount > 0 || elevatedCount > 0) && (
            <div className="flex items-center gap-1.5">
              {overloadedCount > 0 && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                  🔴 {overloadedCount} перегруз
                </span>
              )}
              {elevatedCount > 0 && (
                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
                  🟡 {elevatedCount} повышенная
                </span>
              )}
            </div>
          )}

          {/* Problem manager names preview */}
          {problemManagers.length > 0 && !isExpanded && (
            <span className="text-xs text-gray-500">
              ({problemManagers.slice(0, 3).map(m => m.name?.split(' ')[0]).join(', ')}
              {problemManagers.length > 3 && ` +${problemManagers.length - 3}`})
            </span>
          )}
        </div>

        {/* Right part - checkbox and expand icon */}
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-500">
            <Checkbox
              checked={filters.only_overloaded}
              onCheckedChange={(v) => onFilterChange('only_overloaded', v)}
              className="h-3.5 w-3.5"
              data-testid="filter-only-overloaded"
            />
            Только перегруженные
          </label>
          <div
            className="cursor-pointer p-1 hover:bg-gray-100 rounded"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-3 pb-3 border-t bg-gray-50/50">
          {/* Status summary row */}
          <div className="flex items-center gap-2 py-2">
            {Object.entries(WORKLOAD_CONFIG).map(([status, config]) => {
              const count = teamWorkload.summary?.[status] || 0;
              return (
                <div
                  key={status}
                  className={cn(
                    "flex-1 py-1.5 px-2 rounded text-center text-xs",
                    config.color
                  )}
                >
                  <div className="font-bold">{count}</div>
                  <div className="opacity-70">{config.emoji} {config.label}</div>
                </div>
              );
            })}
          </div>

          {/* Manager cards */}
          {problemManagers.length > 0 && (
            <div className="grid grid-cols-6 gap-2 pt-2 border-t">
              {problemManagers.slice(0, 6).map(manager => {
                const config = WORKLOAD_CONFIG[manager.workload_status] || WORKLOAD_CONFIG.normal;
                const isSelected = filters.responsible_id === manager.id;
                return (
                  <div
                    key={manager.id}
                    className={cn(
                      "p-2 rounded text-center cursor-pointer transition-all hover:shadow-md text-xs",
                      config.color,
                      isSelected && "ring-2 ring-indigo-500"
                    )}
                    onClick={() => onFilterChange('responsible_id', isSelected ? '' : manager.id)}
                    title={`${manager.name}: ${manager.project_count} проектов`}
                  >
                    <div className="font-medium truncate">{manager.name?.split(' ')[0]}</div>
                    <div className="text-base font-bold">{manager.project_count}</div>
                    {manager.critical_count > 0 && (
                      <div className="text-red-600">🔴 {manager.critical_count}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// === MAIN COMPONENT ===

const ReportBuilderPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  // Проверка доступа к финансовым блокам (Затраты, Маржинальность)
  // Скрыто для Senior Manager
  const userRoleLower = user?.role?.toLowerCase();
  const canViewFinanceBlocks = userRoleLower === 'admin' || userRoleLower === 'finance_admin';

  // Фильтрованные колонки для таблицы (26 полей для Senior Manager, все 34 для Admin/Finance Admin)
  const filteredTableColumns = useMemo(() => {
    if (canViewFinanceBlocks) return TABLE_COLUMNS;
    // Senior Manager видит только колонки без флага finance (блоки 1-4)
    return TABLE_COLUMNS.filter(col => !col.finance);
  }, [canViewFinanceBlocks]);

  // Фильтрованные опции для Pivot
  const filteredPivotValueOptions = useMemo(() => {
    if (canViewFinanceBlocks) return PIVOT_VALUE_OPTIONS;
    return PIVOT_VALUE_OPTIONS.filter(opt => !FINANCE_PIVOT_VALUES.includes(opt.value));
  }, [canViewFinanceBlocks]);

  // Фильтрованные шаблоны Pivot (исключаем financial для Senior Manager)
  const filteredPivotTemplates = useMemo(() => {
    if (canViewFinanceBlocks) return PIVOT_TEMPLATES;
    return PIVOT_TEMPLATES.filter(t => t.id !== 'financial').map(t => ({
      ...t,
      config: {
        ...t.config,
        values: t.config.values.filter(v => !FINANCE_PIVOT_VALUES.includes(v)),
      },
    }));
  }, [canViewFinanceBlocks]);

  // Parse URL params to restore filters
  const getInitialFilters = () => {
    const responsible = searchParams.get('responsible_id') || '';
    const client = searchParams.get('client_name') || '';
    const vendor = searchParams.get('vendor_name') || '';
    const healthParam = searchParams.get('health');
    const health = healthParam ? healthParam.split(',') : ['green', 'yellow', 'red'];
    const lifecycle = searchParams.get('lifecycle') || 'all';
    const onlyProblems = searchParams.get('only_problems') === 'true';
    const onlyOverloaded = searchParams.get('only_overloaded') === 'true';
    const period = searchParams.get('period') || 'all';

    return {
      client_name: client,
      responsible_id: responsible,
      vendor_name: vendor,
      health_statuses: health,
      lifecycle,
      date_from: null,
      date_to: null,
      only_problems: onlyProblems,
      only_overloaded: onlyOverloaded,
      period,
    };
  };

  // View mode
  const [viewMode, setViewMode] = useState(() => searchParams.get('view') || 'table');

  // Sprint D: Report mode (INTERNAL = all data, EXTERNAL = client-safe whitelist only)
  const [reportMode, setReportMode] = useState(() => searchParams.get('report_mode') || 'INTERNAL');

  // Filter options from API
  const [filterOptions, setFilterOptions] = useState({
    employees: [],
    clients: [],
    vendors: [],
  });
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Team workload data (P0 BI-ADVANCED)
  const [teamWorkload, setTeamWorkload] = useState(null);
  const [workloadLoading, setWorkloadLoading] = useState(true);

  // Filters - initialize from URL
  const [filters, setFilters] = useState(getInitialFilters);
  
  // Table columns
  const [selectedColumns, setSelectedColumns] = useState(() => {
    const initial = {};
    TABLE_COLUMNS.forEach(col => { initial[col.key] = col.default; });
    return initial;
  });
  
  // Pivot config
  const [pivotConfig, setPivotConfig] = useState({
    rows: ['client_name'],
    values: ['kp_amount_with_vat', 'count'],
    aggregation: 'sum',
  });
  
  // Report data
  const [reportData, setReportData] = useState(null);
  const [generating, setGenerating] = useState(false);
  
  // Presets
  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');

  // Track if initial load is done
  const [initialized, setInitialized] = useState(false);

  // Load filter options, presets and team workload on mount
  useEffect(() => {
    const init = async () => {
      await Promise.all([
        loadFilterOptions(),
        loadPresets(),
        loadTeamWorkload(),
      ]);
      setInitialized(true);
    };
    init();
  }, []);

  // Load team workload data
  const loadTeamWorkload = async () => {
    try {
      setWorkloadLoading(true);
      const data = await reportsAPI.getTeamWorkload();
      setTeamWorkload(data);
    } catch (error) {
      console.error('Failed to load team workload:', error);
    } finally {
      setWorkloadLoading(false);
    }
  };

  // Sync filters to URL (for back navigation)
  useEffect(() => {
    if (!initialized) return;

    const params = new URLSearchParams();

    if (filters.responsible_id) params.set('responsible_id', filters.responsible_id);
    if (filters.client_name) params.set('client_name', filters.client_name);
    if (filters.vendor_name) params.set('vendor_name', filters.vendor_name);
    if (filters.health_statuses.length > 0 && filters.health_statuses.length < 3) {
      params.set('health', filters.health_statuses.join(','));
    }
    if (filters.lifecycle !== 'all') params.set('lifecycle', filters.lifecycle);
    if (filters.only_problems) params.set('only_problems', 'true');
    if (filters.only_overloaded) params.set('only_overloaded', 'true');
    if (filters.period && filters.period !== 'all') params.set('period', filters.period);
    if (viewMode !== 'table') params.set('view', viewMode);
    if (reportMode !== 'INTERNAL') params.set('report_mode', reportMode);

    // Update URL without triggering navigation
    setSearchParams(params, { replace: true });
  }, [initialized, filters, viewMode, reportMode, setSearchParams]);

  // Silent report generation (no success toast, for auto-refresh)
  const generateReportSilent = useCallback(async () => {
    try {
      setGenerating(true);
      const request = {
        filters: {
          client_name: filters.client_name || null,
          responsible_id: filters.responsible_id || null,
          vendor_name: filters.vendor_name || null,
          health_statuses: filters.health_statuses.length > 0 ? filters.health_statuses : null,
          lifecycle: filters.lifecycle,
          date_from: filters.date_from ? format(filters.date_from, 'yyyy-MM-dd') : null,
          date_to: filters.date_to ? format(filters.date_to, 'yyyy-MM-dd') : null,
          only_problems: filters.only_problems,
          only_overloaded: filters.only_overloaded,
          period: filters.period || null,
        },
        view_mode: viewMode,
        pivot_config: viewMode === 'pivot' ? pivotConfig : null,
        report_mode: reportMode,  // Sprint D: INTERNAL or EXTERNAL
      };
      const data = await reportsAPI.generateReport(request);
      setReportData(data);
    } catch (error) {
      console.error('Auto-generate failed:', error);
    } finally {
      setGenerating(false);
    }
  }, [filters, viewMode, pivotConfig, reportMode]);

  // Auto-generate report when filters change (after initialization)
  useEffect(() => {
    if (!initialized) return;

    // Debounce to avoid too many API calls
    const timer = setTimeout(() => {
      generateReportSilent();
    }, 300);

    return () => clearTimeout(timer);
  }, [initialized, generateReportSilent]);

  const loadFilterOptions = async () => {
    try {
      setLoadingOptions(true);
      const data = await reportsAPI.getFilterOptions();
      setFilterOptions(data);
    } catch (error) {
      showError(error, 'Ошибка загрузки фильтров');
    } finally {
      setLoadingOptions(false);
    }
  };

  const loadPresets = async () => {
    try {
      setPresetsLoading(true);
      const data = await reportsAPI.getPresets();
      setPresets(data);
    } catch (error) {
      console.error('Failed to load presets:', error);
    } finally {
      setPresetsLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleHealthToggle = (status) => {
    setFilters(prev => {
      const current = prev.health_statuses || [];
      return {
        ...prev,
        health_statuses: current.includes(status)
          ? current.filter(s => s !== status)
          : [...current, status]
      };
    });
  };

  const handleColumnToggle = (key) => {
    setSelectedColumns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handlePivotRowToggle = (value) => {
    setPivotConfig(prev => {
      const current = prev.rows || [];
      return {
        ...prev,
        rows: current.includes(value)
          ? current.filter(r => r !== value)
          : [...current, value]
      };
    });
  };

  const handlePivotValueToggle = (value) => {
    setPivotConfig(prev => {
      const current = prev.values || [];
      return {
        ...prev,
        values: current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value]
      };
    });
  };

  // Manual refresh (same as auto but can be triggered by button)
  const generateReport = async () => {
    await generateReportSilent();
  };

  // === PRESETS ===
  
  const applyPreset = (preset) => {
    setViewMode(preset.view_mode || 'table');
    setFilters({
      client_name: preset.filters?.client_name || '',
      responsible_id: preset.filters?.responsible_id || '',
      vendor_name: preset.filters?.vendor_name || '',
      health_statuses: preset.filters?.health_statuses || ['green', 'yellow', 'red'],
      lifecycle: preset.filters?.lifecycle || 'all',
      date_from: preset.filters?.date_from ? new Date(preset.filters.date_from) : null,
      date_to: preset.filters?.date_to ? new Date(preset.filters.date_to) : null,
      only_problems: preset.filters?.only_problems || false,
    });
    if (preset.table_columns?.length > 0) {
      const cols = {};
      // Используем filteredTableColumns для Senior Manager
      filteredTableColumns.forEach(col => { cols[col.key] = preset.table_columns.includes(col.key); });
      setSelectedColumns(cols);
    }
    if (preset.pivot_rows?.length > 0 || preset.pivot_values?.length > 0) {
      setPivotConfig({
        rows: preset.pivot_rows || ['client_name'],
        values: preset.pivot_values || ['kp_amount_with_vat', 'count'],
        aggregation: preset.pivot_aggregation || 'sum',
      });
    }
    showSuccess(`Пресет «${preset.name}» применён`);
  };

  const savePreset = async () => {
    if (!presetName.trim()) {
      showError(null, 'Введите название пресета');
      return;
    }
    
    try {
      const preset = {
        name: presetName,
        description: presetDescription || null,
        view_mode: viewMode,
        filters: {
          client_name: filters.client_name || null,
          responsible_id: filters.responsible_id || null,
          vendor_name: filters.vendor_name || null,
          health_statuses: filters.health_statuses,
          lifecycle: filters.lifecycle,
          date_from: filters.date_from ? format(filters.date_from, 'yyyy-MM-dd') : null,
          date_to: filters.date_to ? format(filters.date_to, 'yyyy-MM-dd') : null,
          only_problems: filters.only_problems,
        },
        table_columns: Object.entries(selectedColumns).filter(([_, v]) => v).map(([k]) => k),
        pivot_rows: pivotConfig.rows,
        pivot_values: pivotConfig.values,
        pivot_aggregation: pivotConfig.aggregation,
      };
      
      await reportsAPI.createPreset(preset);
      showSuccess('Пресет сохранён');
      setSavePresetOpen(false);
      setPresetName('');
      setPresetDescription('');
      loadPresets();
    } catch (error) {
      showError(error, 'Ошибка сохранения пресета');
    }
  };

  const deletePreset = async (presetId) => {
    try {
      await reportsAPI.deletePreset(presetId);
      showSuccess('Пресет удалён');
      loadPresets();
    } catch (error) {
      showError(error, 'Ошибка удаления пресета');
    }
  };

  // === EXPORT ===

  // Очистка BB-кодов Bitrix24 из текста
  const cleanBBCode = (text) => {
    if (!text || typeof text !== 'string') return text || '';
    return text
      // Удаляем BB-теги с содержимым для TABLE (таблицы нечитаемы в тексте)
      .replace(/\[TABLE\][\s\S]*?\[\/TABLE\]/gi, '')
      // Удаляем BB-теги с атрибутами
      .replace(/\[url=[^\]]*\](.*?)\[\/url\]/gi, '$1')
      .replace(/\[user=\d+\](.*?)\[\/user\]/gi, '$1')
      .replace(/\[quote\][^[]*\[\/quote\]/gi, '')
      // Удаляем простые BB-теги, сохраняя содержимое
      .replace(/\[\/?(b|i|u|s|list|quote|code|url|img|color|size|font|table|tr|td|th|hr|p)\]/gi, '')
      .replace(/\[\*\]/g, '• ')
      // Очищаем лишние пробелы и переносы
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const exportToExcel = async () => {
    if (!reportData || reportData.projects?.length === 0) {
      showError(null, 'Нет данных для экспорта');
      return;
    }

    // Используем filteredTableColumns чтобы скрыть финансовые колонки для Senior Manager
    const activeCols = filteredTableColumns.filter(col => selectedColumns[col.key]);

    // Определяем колонки с валютами
    const currencyColumns = ['budget_wo_vat', 'margin_amount', 'kp_amount', 'kp_amount_with_vat', 'contract_amount_with_vat', 'budget_plan', 'budget_fact', 'budget_delta'];

    // Создаём workbook с ExcelJS
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Отчёт');

    // Заголовки колонок (с № п/п)
    const columnHeaders = ['п/п', ...activeCols.map(col => col.label)];

    // Формируем заголовок отчёта
    const reportDate = format(new Date(), 'dd.MM.yyyy', { locale: ru });
    const clientName = filters.client_name || 'Все заказчики';

    // Строка 1: Заголовок отчёта (объединённые ячейки)
    worksheet.mergeCells(1, 1, 1, columnHeaders.length);
    const titleCell = worksheet.getCell(1, 1);
    titleCell.value = `Отчёт: ${clientName} на ${reportDate}`;
    titleCell.font = { name: 'Calibri', bold: true, size: 14 };
    titleCell.alignment = { horizontal: 'left', vertical: 'middle' };

    // Строка 2: Заголовки колонок
    const headerRowNum = 2;
    const headerRow = worksheet.getRow(headerRowNum);
    columnHeaders.forEach((header, idx) => {
      const cell = headerRow.getCell(idx + 1);
      cell.value = header;
      cell.font = { name: 'Calibri', bold: true, size: 11 };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' },
      };
    });

    // Данные проектов
    reportData.projects.forEach((project, idx) => {
      const rowNum = headerRowNum + 1 + idx;
      const row = worksheet.getRow(rowNum);

      // п/п
      const numCell = row.getCell(1);
      numCell.value = idx + 1;
      numCell.font = { name: 'Calibri', size: 11 };
      numCell.alignment = { horizontal: 'center', vertical: 'top' };
      numCell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };

      // Остальные колонки
      activeCols.forEach((col, colIdx) => {
        const cell = row.getCell(colIdx + 2);
        let value = project[col.key];

        // Форматирование значений
        if (currencyColumns.includes(col.key)) {
          // Числовой формат для валюты (без символа ₸)
          if (value != null && value !== '') {
            const num = parseFloat(value);
            cell.value = !isNaN(num) ? num : '';
            cell.numFmt = '#,##0.00';
          }
        } else if (col.key === 'margin_percent') {
          cell.value = value != null ? value / 100 : '';
          cell.numFmt = '0%';
        } else if (col.key === 'expected_end_date') {
          cell.value = value ? formatDateShort(value) : '';
        } else if (col.key === 'health_status') {
          cell.value = HEALTH_CONFIG[value]?.label || value || '';
        } else if (col.key === 'indicators') {
          cell.value = project.indicators?.map(i => i.label).join(', ') || '';
        } else if (col.key === 'description') {
          // Очищаем BB-коды Bitrix24 из описания
          cell.value = cleanBBCode(value);
        } else {
          cell.value = value ?? '';
        }

        // Шрифт для всех ячеек данных
        cell.font = { name: 'Calibri', size: 11 };

        // Выравнивание и перенос текста
        cell.alignment = {
          horizontal: currencyColumns.includes(col.key) ? 'right' : 'left',
          vertical: 'top',
          wrapText: true,
        };

        // Границы
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    });

    // Ширина колонок
    worksheet.columns = columnHeaders.map((header, idx) => {
      if (idx === 0) return { width: 5 }; // п/п
      const col = activeCols[idx - 1];
      if (col.key === 'description') return { width: 60 }; // Описание - широкая
      if (col.key === 'name') return { width: 35 }; // Наименование
      if (currencyColumns.includes(col.key)) return { width: 18 }; // Валюта
      if (col.key === 'project_status') return { width: 25 }; // Статус
      return { width: 20 }; // Default
    });

    // Сохраняем файл
    const buffer = await workbook.xlsx.writeBuffer();
    const dateStr = format(new Date(), 'dd.MM.yyyy');
    const safeClientName = (filters.client_name || 'all').replace(/[^a-zA-Zа-яА-ЯёЁ0-9]/g, '_');
    saveAs(new Blob([buffer]), `${safeClientName}_${dateStr}.xlsx`);
    showSuccess('Отчёт скачан');
  };

  // === KPI SUMMARY ===
  
  const kpiSummary = useMemo(() => {
    if (!reportData?.projects) return null;

    const projects = reportData.projects;
    const totalBudget = projects.reduce((sum, p) => sum + (p.kp_amount_with_vat || 0), 0);
    const totalMargin = projects.reduce((sum, p) => sum + (p.margin_amount || 0), 0);
    const problemCount = projects.filter(p => p.is_problem).length;
    const avgMargin = projects.filter(p => p.margin_percent != null).reduce((sum, p, _, arr) => sum + p.margin_percent / arr.length, 0);

    // Budget Plan/Fact/Delta (P1 BI-ADVANCED)
    const totalPlan = projects.reduce((sum, p) => sum + (p.budget_plan || 0), 0);
    const totalFact = projects.reduce((sum, p) => sum + (p.budget_fact || 0), 0);
    const totalDelta = totalFact - totalPlan;
    const deltaPercent = totalPlan > 0 ? Math.round((totalDelta / totalPlan) * 100) : 0;

    return {
      total: projects.length,
      totalBudget,
      totalMargin,
      problemCount,
      avgMargin: Math.round(avgMargin * 100) / 100,
      // Budget KPIs
      totalPlan,
      totalFact,
      totalDelta,
      deltaPercent,
    };
  }, [reportData]);

  // === RENDER ===
  
  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-50" data-testid="report-builder-page">
      {/* Header */}
      <header className="bg-white shadow-sm border-b flex-shrink-0">
        <div className="max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Назад
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <FileBarChart className="w-6 h-6" />
                  Конструктор отчётов
                </h1>
                <p className="text-sm text-gray-600">Аналитика проектов, финансов и нагрузки</p>
              </div>
            </div>
            
            {/* View Mode Tabs */}
            <Tabs value={viewMode} onValueChange={setViewMode}>
              <TabsList>
                <TabsTrigger value="table" className="flex items-center gap-1">
                  <TableProperties className="w-4 h-4" />
                  Таблица
                </TabsTrigger>
                <TabsTrigger value="pivot" className="flex items-center gap-1">
                  <PieChart className="w-4 h-4" />
                  Сводная
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Sprint D: Report Mode Toggle (INTERNAL/EXTERNAL) */}
            <Tabs value={reportMode} onValueChange={setReportMode}>
              <TabsList>
                <TabsTrigger value="INTERNAL" className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  Полный
                </TabsTrigger>
                <TabsTrigger value="EXTERNAL" className="flex items-center gap-1">
                  <EyeOff className="w-4 h-4" />
                  Для клиента
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-auto mx-auto w-full px-4 sm:px-6 lg:px-8 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          
          {/* === SIDEBAR === */}
          <div className="lg:col-span-1 space-y-4">
            
            {/* Presets */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FolderOpen className="w-4 h-4" />
                  Шаблоны
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {presets.length === 0 ? (
                  <p className="text-xs text-gray-400">Нет сохранённых шаблонов</p>
                ) : (
                  <ScrollArea className="h-[120px]">
                    {presets.map(preset => (
                      <div key={preset.id} className="flex items-center justify-between py-1 group">
                        <button
                          onClick={() => applyPreset(preset)}
                          className="text-sm text-blue-600 hover:underline truncate max-w-[140px]"
                          data-testid={`preset-${preset.id}`}
                        >
                          {preset.name}
                        </button>
                        <button
                          onClick={() => deletePreset(preset.id)}
                          className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </ScrollArea>
                )}
                <Button size="sm" variant="outline" className="w-full" onClick={() => setSavePresetOpen(true)}>
                  <Save className="w-3 h-3 mr-1" />
                  Сохранить текущий
                </Button>
              </CardContent>
            </Card>

            {/* Filters */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Фильтры
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Client */}
                <div>
                  <Label className="text-xs">Заказчик</Label>
                  <Select 
                    value={filters.client_name} 
                    onValueChange={(v) => handleFilterChange('client_name', v === 'all' ? '' : v)}
                  >
                    <SelectTrigger className="h-8 text-xs" data-testid="filter-client">
                      <SelectValue placeholder="Все" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Все заказчики</SelectItem>
                      {filterOptions.clients?.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Responsible */}
                <div>
                  <Label className="text-xs">Ответственный</Label>
                  <Select 
                    value={filters.responsible_id} 
                    onValueChange={(v) => handleFilterChange('responsible_id', v === 'all' ? '' : v)}
                  >
                    <SelectTrigger className="h-8 text-xs" data-testid="filter-responsible">
                      <SelectValue placeholder="Все" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Все менеджеры</SelectItem>
                      {filterOptions.employees?.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Health Status */}
                <div>
                  <Label className="text-xs">Статус здоровья</Label>
                  <div className="flex flex-col gap-1 mt-1">
                    {['red', 'yellow', 'green'].map(status => (
                      <button
                        key={status}
                        onClick={() => handleHealthToggle(status)}
                        className={cn(
                          "px-2 py-1 text-xs rounded border transition-colors text-left flex items-center gap-2",
                          filters.health_statuses.includes(status)
                            ? HEALTH_CONFIG[status].color + " border-current"
                            : "bg-gray-100 text-gray-400 border-gray-200"
                        )}
                        data-testid={`filter-health-${status}`}
                      >
                        <span>{HEALTH_CONFIG[status].emoji}</span>
                        <span>{HEALTH_CONFIG[status].label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Lifecycle */}
                <div>
                  <Label className="text-xs">Жизн. цикл</Label>
                  <Select
                    value={filters.lifecycle}
                    onValueChange={(v) => handleFilterChange('lifecycle', v)}
                  >
                    <SelectTrigger className="h-8 text-xs" data-testid="filter-lifecycle">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Все</SelectItem>
                      <SelectItem value="active">Активные</SelectItem>
                      <SelectItem value="done">Завершённые</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Period (P2 BI-ADVANCED) */}
                <div>
                  <Label className="text-xs">Период</Label>
                  <Select
                    value={filters.period || 'all'}
                    onValueChange={(v) => handleFilterChange('period', v)}
                  >
                    <SelectTrigger className="h-8 text-xs" data-testid="filter-period">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PERIOD_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Date Range */}
                <div>
                  <Label className="text-xs">Период дедлайна</Label>
                  <div className="grid grid-cols-2 gap-1 mt-1">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7 text-xs justify-start">
                          <CalendarIcon className="w-3 h-3 mr-1" />
                          {filters.date_from ? format(filters.date_from, 'dd.MM') : 'От'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={filters.date_from} onSelect={(d) => handleFilterChange('date_from', d)} locale={ru} />
                      </PopoverContent>
                    </Popover>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7 text-xs justify-start">
                          <CalendarIcon className="w-3 h-3 mr-1" />
                          {filters.date_to ? format(filters.date_to, 'dd.MM') : 'До'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={filters.date_to} onSelect={(d) => handleFilterChange('date_to', d)} locale={ru} />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                
                {/* Only Problems */}
                <label className="flex items-center gap-2 cursor-pointer pt-2 border-t">
                  <Checkbox
                    checked={filters.only_problems}
                    onCheckedChange={(v) => handleFilterChange('only_problems', v)}
                    data-testid="filter-only-problems"
                  />
                  <span className="text-xs">Только проблемные</span>
                  <AlertTriangle className="w-3 h-3 text-red-500" />
                </label>
              </CardContent>
            </Card>

            {/* Columns / Pivot Config */}
            {viewMode === 'table' ? (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Колонки</CardTitle>
                </CardHeader>
                <CardContent>
                  {/* На узком экране - прокрутка с max-height, на широком (xl+) - все колонки видны */}
                  <div className="max-h-[200px] xl:max-h-none overflow-y-auto xl:overflow-visible">
                    <div className="space-y-1">
                      {filteredTableColumns.map(col => (
                        <label key={col.key} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5">
                          <Checkbox
                            checked={selectedColumns[col.key]}
                            onCheckedChange={() => handleColumnToggle(col.key)}
                          />
                          <span className="text-xs">{col.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Настройка сводной</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Quick Templates (P2 BI-ADVANCED) - фильтрованы для Senior Manager */}
                  <div>
                    <Label className="text-xs font-medium mb-2 block">Быстрые шаблоны</Label>
                    <div className="grid grid-cols-2 gap-1">
                      {filteredPivotTemplates.map(tpl => (
                        <button
                          key={tpl.id}
                          onClick={() => setPivotConfig(tpl.config)}
                          className={cn(
                            "p-2 text-xs rounded border transition-all hover:bg-gray-50",
                            JSON.stringify(pivotConfig) === JSON.stringify(tpl.config)
                              ? "border-indigo-400 bg-indigo-50 text-indigo-700"
                              : "border-gray-200"
                          )}
                          data-testid={`pivot-template-${tpl.id}`}
                        >
                          <span className="text-base">{tpl.icon}</span>
                          <div className="truncate">{tpl.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <Label className="text-xs font-medium">Строки (группировка)</Label>
                    <div className="space-y-1 mt-1">
                      {PIVOT_ROW_OPTIONS.map(opt => (
                        <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={pivotConfig.rows.includes(opt.value)}
                            onCheckedChange={() => handlePivotRowToggle(opt.value)}
                          />
                          <span className="text-xs">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <Label className="text-xs font-medium">Значения (метрики)</Label>
                    <div className="space-y-1 mt-1">
                      {filteredPivotValueOptions.map(opt => (
                        <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={pivotConfig.values.includes(opt.value)}
                            onCheckedChange={() => handlePivotValueToggle(opt.value)}
                          />
                          <span className="text-xs">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <Label className="text-xs font-medium">Агрегация</Label>
                    <Select
                      value={pivotConfig.aggregation}
                      onValueChange={(v) => setPivotConfig(prev => ({ ...prev, aggregation: v }))}
                    >
                      <SelectTrigger className="h-7 text-xs mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AGGREGATION_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* === MAIN CONTENT === */}
          <div className="lg:col-span-4 space-y-4">

            {/* Team Workload Panel - Collapsible */}
            {teamWorkload && <CollapsibleWorkloadPanel
              teamWorkload={teamWorkload}
              filters={filters}
              onFilterChange={handleFilterChange}
            />}

            {/* KPI Stats Bar + Finance Dashboard */}
            {kpiSummary && (
              <div className="flex items-center gap-4 px-3 py-2 bg-gray-50 rounded-lg border">
                {/* Projects count */}
                <div className="flex items-center gap-1.5">
                  <BarChart3 className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs text-gray-500">Проектов:</span>
                  <span className="text-sm font-semibold">{kpiSummary.total}</span>
                </div>

                <div className="h-4 w-px bg-gray-300" />

                {/* Budget */}
                <div className="flex items-center gap-1.5">
                  <span className="w-3.5 h-3.5 flex items-center justify-center font-bold text-[10px] text-blue-500">₸</span>
                  <span className="text-xs text-gray-500">Бюджет:</span>
                  <span className="text-sm font-semibold text-blue-600">{formatCurrency(kpiSummary.totalBudget)}</span>
                </div>

                <div className="h-4 w-px bg-gray-300" />

                {/* Problems - clickable */}
                <button
                  className={cn(
                    "flex items-center gap-1.5 px-2 py-0.5 rounded transition-all",
                    filters.only_problems
                      ? "bg-red-100 ring-1 ring-red-300"
                      : kpiSummary.problemCount > 0
                        ? "hover:bg-red-50"
                        : "hover:bg-gray-100"
                  )}
                  onClick={() => handleFilterChange('only_problems', !filters.only_problems)}
                  title={filters.only_problems ? "Показать все проекты" : "Показать только проблемные"}
                  data-testid="kpi-problems-card"
                >
                  <AlertTriangle className={cn("w-3.5 h-3.5", kpiSummary.problemCount > 0 || filters.only_problems ? "text-red-500" : "text-gray-400")} />
                  <span className="text-xs text-gray-500">В риске:</span>
                  <span className={cn("text-sm font-semibold", (kpiSummary.problemCount > 0 || filters.only_problems) && "text-red-600")}>
                    {kpiSummary.problemCount}
                  </span>
                  {filters.only_problems && <span className="text-xs text-red-500">✓</span>}
                </button>

                {/* Finance Dashboard (P0 BI-ADVANCED Finance Control) - скрыто для Senior Manager */}
                {canViewFinanceBlocks && (
                  <>
                    <div className="h-4 w-px bg-gray-300" />
                    <FinanceDashboard
                      summary={reportData?.finance_summary}
                      onDrillDown={(metric) => {
                        // Drill-down: switch to table view and highlight column
                        setViewMode('table');
                        // Enable relevant column based on metric
                        if (metric === 'hardware_margin' || metric === 'service_margin' || metric === 'total_margin') {
                          setSelectedColumns(prev => ({ ...prev, margin_amount: true, margin_percent: true }));
                        } else if (metric === 'budget_plan' || metric === 'costs_fact' || metric === 'budget_fact') {
                          setSelectedColumns(prev => ({
                            ...prev,
                            budget_plan: true,
                            budget_fact: true,
                            budget_delta: true,
                          }));
                        }
                      }}
                    />
                  </>
                )}
              </div>
            )}

            {/* Report Card */}
            <Card>
              <CardHeader className="border-b py-2 px-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      {viewMode === 'table' ? <Table className="w-4 h-4" /> : <PieChart className="w-4 h-4" />}
                      {viewMode === 'table' ? 'Таблица' : 'Сводная'}
                      <span className="text-sm font-normal text-gray-500">
                        {reportData ? `${reportData.count} проектов` : ''}
                      </span>
                    </CardTitle>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={generateReport} disabled={generating} data-testid="generate-report-btn">
                      <RefreshCw className={cn("w-4 h-4 mr-1", generating && "animate-spin")} />
                      {generating ? 'Загрузка...' : 'Сформировать'}
                    </Button>
                    <Button variant="outline" onClick={exportToExcel} disabled={!reportData?.projects?.length}>
                      <Download className="w-4 h-4 mr-1" />
                      XLSX
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {!reportData ? (
                  <div className="p-12 text-center text-gray-400">
                    <FileBarChart className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p>Настройте фильтры и нажмите «Сформировать»</p>
                  </div>
                ) : reportData.count === 0 ? (
                  <div className="p-12 text-center text-gray-400">
                    <p>Нет данных по заданным фильтрам</p>
                  </div>
                ) : viewMode === 'table' ? (
                  <TableView data={reportData} columns={filteredTableColumns.filter(c => selectedColumns[c.key])} />
                ) : (
                  <PivotView data={reportData} />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Save Preset Dialog */}
      <Dialog open={savePresetOpen} onOpenChange={setSavePresetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Сохранить шаблон</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Название</Label>
              <Input 
                value={presetName} 
                onChange={(e) => setPresetName(e.target.value)} 
                placeholder="Например: Финансы ГП"
              />
            </div>
            <div>
              <Label>Описание (опционально)</Label>
              <Input 
                value={presetDescription} 
                onChange={(e) => setPresetDescription(e.target.value)} 
                placeholder="Краткое описание"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSavePresetOpen(false)}>Отмена</Button>
            <Button onClick={savePreset}>Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// === TABLE VIEW COMPONENT ===

const TableView = ({ data, columns }) => {
  // Функция форматирования ячеек
  const formatCell = useCallback((project, key) => {
    const value = project[key];
    switch (key) {
      // Финансовые поля (денежные суммы)
      case 'client_budget_with_vat':
      case 'kp_amount_with_vat':
      case 'gpl_project_with_vat':
      case 'entry_cost_with_vat':
      case 'contract_amount_with_vat':
      case 'vendor_payment_fact_amount':
      case 'expenses_equipment':
      case 'expenses_logistics':
      case 'expenses_other':
      case 'expenses_installation':
      case 'expenses_overtime':
      case 'expenses_insurance':
      case 'expenses_credit':
      case 'expenses_partnership':
      case 'expenses_penalty':
      case 'subcontract_amount':
      case 'taxes_amount':
      case 'result_margin':
      case 'result_taxes':
      case 'result_gross_profit':
      case 'budget_plan':
      case 'budget_fact':
      case 'costs_fact':
        return value != null ? formatCurrency(value) : '—';

      // Проценты
      case 'subcontract_percent':
      case 'result_margin_percent':
        return value != null ? `${value}%` : '—';

      // Риски (1-5)
      case 'risk_probability':
      case 'risk_impact':
        if (value == null) return '—';
        const riskLevel = parseInt(value);
        if (riskLevel >= 4) return <span className="text-red-600 font-medium">{value}</span>;
        if (riskLevel >= 3) return <span className="text-yellow-600">{value}</span>;
        return value;

      // Категория риска
      case 'risk_category':
        if (!value || value === 'none') return '—';
        return value;

      // Даты
      case 'expected_end_date':
      case 'contract_sign_date':
      case 'contract_close_date':
      case 'vendor_payment_plan_date':
      case 'client_payment_fact_date':
      case 'delivery_eta':
      case 'installation_start':
      case 'ts_deadline':
        return value ? formatDateShort(value) : '—';

      // Год
      case 'year':
        return value || '—';

      // Статус проекта с цветом
      case 'project_status':
        if (!value) return <span className="text-red-500">Не указан</span>;
        const statusColors = {
          'Предпроект': 'bg-gray-100 text-gray-800',
          'Проект': 'bg-blue-100 text-blue-800',
          'Завершен': 'bg-green-100 text-green-800',
          'Отменен': 'bg-red-100 text-red-800',
        };
        return (
          <Badge className={statusColors[value] || 'bg-gray-100 text-gray-800'}>
            {value}
          </Badge>
        );

      // Статус ТС
      case 'ts_status':
        if (!value) return '—';
        const tsColors = {
          'готовится': 'bg-yellow-100 text-yellow-800',
          'отдали': 'bg-blue-100 text-blue-800',
          'в проработке': 'bg-purple-100 text-purple-800',
        };
        return (
          <Badge className={tsColors[value] || 'bg-gray-100 text-gray-800'}>
            {value}
          </Badge>
        );

      // Соисполнители (массив имен)
      case 'co_executors':
        if (!value || (Array.isArray(value) && value.length === 0)) return '—';
        if (Array.isArray(value)) {
          const names = value.join(', ');
          return names.length > 30 ? (
            <span title={names}>{names.substring(0, 30)}...</span>
          ) : names;
        }
        return value;

      // Менеджер с индикатором нагрузки
      case 'manager':
        if (!value) return '—';
        const workloadCfg = WORKLOAD_CONFIG[project.manager_workload_status] || WORKLOAD_CONFIG.normal;
        return (
          <span className="flex items-center gap-1">
            {value}
            {project.manager_workload > 8 && (
              <span
                className="text-xs cursor-help"
                title={`${workloadCfg.label}: ${project.manager_workload} проектов`}
              >
                {workloadCfg.emoji}
              </span>
            )}
          </span>
        );

      // Bitrix ID
      case 'bitrix_task_id':
        return value || '—';

      // Вендор - пустая строка тоже "—"
      case 'vendor_name':
        return value || '—';

      // Поставщик
      case 'supplier_name':
        return value || '—';

      // Название проекта - должно быть всегда
      case 'name':
        return value || 'Без названия';

      default:
        // Для всех остальных: null, undefined, пустая строка → "—"
        if (value === null || value === undefined || value === '') return '—';
        return value;
    }
  }, []);

  return (
    <ResizableTable
      data={data}
      columns={columns}
      formatCell={formatCell}
      storageKey="report-builder-table"
    />
  );
};


// === PIVOT VIEW COMPONENT ===

const PivotView = ({ data }) => {
  if (!data.pivot) return <p className="p-4 text-gray-400">Нет данных для сводки</p>;
  
  const { rows, totals, config } = data.pivot;
  
  const formatValue = (key, value) => {
    if (key === 'count') return value;
    if (key === 'budget_wo_vat' || key === 'margin' || key === 'kp_amount' || key === 'kp_amount_with_vat' || key === 'contract_amount_with_vat') {
      return formatCurrency(value);
    }
    return value;
  };

  const valueLabels = {
    kp_amount_with_vat: 'КП с НДС',
    contract_amount_with_vat: 'Договор с НДС',
    budget_wo_vat: 'Бюджет без НДС',
    margin: 'Маржа',
    kp_amount: 'Сумма КП',
    count: 'Кол-во',
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm" data-testid="pivot-table">
        <thead className="bg-gray-50 border-b">
          <tr>
            {config.rows.map(r => (
              <th key={r} className="text-left py-2 px-3 text-xs font-medium text-gray-500 whitespace-nowrap">
                {PIVOT_ROW_OPTIONS.find(o => o.value === r)?.label || r}
              </th>
            ))}
            {config.values.map(v => (
              <th key={v} className="text-right py-2 px-3 text-xs font-medium text-gray-500 whitespace-nowrap">
                {valueLabels[v] || v}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="border-b hover:bg-gray-50">
              {row.key.map((k, i) => (
                <td key={i} className="py-2 px-3 font-medium whitespace-nowrap">
                  {k}
                  {row.is_overloaded && i === config.rows.indexOf('responsible_name') && (
                    <span className="ml-1" title={`${row.workload} проектов`}>🔥</span>
                  )}
                </td>
              ))}
              {config.values.map(v => (
                <td key={v} className={cn(
                  "py-2 px-3 text-right whitespace-nowrap",
                  v === 'margin' && row.values[v] < 0 && "text-red-600 font-medium"
                )}>
                  {formatValue(v, row.values[v])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-gray-100 font-semibold">
          <tr>
            <td colSpan={config.rows.length} className="py-2 px-3 whitespace-nowrap">ИТОГО</td>
            {config.values.map(v => (
              <td key={v} className="py-2 px-3 text-right whitespace-nowrap">
                {formatValue(v, totals.values[v])}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
};


export default ReportBuilderPage;
